import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { validateStats } from '../utils/ocrHelper';
import { parseNBA2KBoxScoreWithCoordinates } from '../utils/nba2kParserCoordinates';
import { extractWithGPT4Vision, isGPT4VisionAvailable } from '../utils/gpt4VisionOcr';
import { extractWithGoogleVision, isGoogleVisionAvailable } from '../utils/googleVisionOcr';
import { extractWithClaudeVision, isClaudeVisionAvailable } from '../utils/claudeVisionOcr';
import { isStaleBuild } from '../utils/buildVersion';

/**
 * Upload der beiden Boxscore-Screenshots.
 *
 * ACHTUNG: Die Komponente wird innerhalb des <form> der Statistikerfassung
 * gerendert. Ein <button> ohne type ist dort laut HTML ein Submit-Button —
 * "Extract Stats" hat deshalb das Formular mit noch leeren Statzeilen
 * abgeschickt, und der Server loescht beim Absenden erst alle vorhandenen
 * Statistiken des Spiels. Jeder Button hier braucht type="button".
 */
const ScreenshotUpload = ({ homeTeam, awayTeam, onExtractComplete, players, homePlayers = [], awayPlayers = [] }) => {
  const { t } = useTranslation();
  const [homeImage, setHomeImage] = useState(null);
  const [awayImage, setAwayImage] = useState(null);
  const [homePreview, setHomePreview] = useState(null);
  const [awayPreview, setAwayPreview] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ home: 0, away: 0 });
  const [error, setError] = useState(null);
  const [ocrMethod, setOcrMethod] = useState('claude'); // Default to Claude Vision
  const [gpt4Available, setGpt4Available] = useState(false);
  const [googleAvailable, setGoogleAvailable] = useState(false);
  const [claudeAvailable, setClaudeAvailable] = useState(false);

  // Check which OCR methods are available
  useEffect(() => {
    
    // Check GPT-4 Vision
    isGPT4VisionAvailable().then((available) => {
      setGpt4Available(available);
    });

    // Check Google Cloud Vision
    isGoogleVisionAvailable().then((available) => {
      setGoogleAvailable(available);
    });

    // Check Claude Vision
    isClaudeVisionAvailable().then((available) => {
      setClaudeAvailable(available);
      
      // Set default method based on what's available
      if (available) {
        setOcrMethod('claude');
      } else {
        setOcrMethod('google');
      }
    });
  }, []);

  const handleFileSelect = (file, team) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image size should be less than 10MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      if (team === 'home') {
        setHomeImage(file);
        setHomePreview(e.target.result);
      } else {
        setAwayImage(file);
        setAwayPreview(e.target.result);
      }
    };
    reader.readAsDataURL(file);
    setError(null);
  };

  const handleDrop = (e, team) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFileSelect(file, team);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const processScreenshots = async () => {
    if (!homeImage || !awayImage) {
      setError('Please upload both home and away team screenshots');
      return;
    }

    // Ein Tab, der seit einem Deploy offen liegt, rechnet noch mit dem alten
    // Namensabgleich — Spieler aus dem Screenshot fallen dann lautlos weg.
    // Lieber hier abbrechen als eine halbe Statistik speichern.
    if (await isStaleBuild()) {
      setError(t('ocrReview.staleBuild'));
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      let homeData, awayData;

      if (ocrMethod === 'google') {
        // Use Google Cloud Vision
        
        // Extract text AND detections (bounding boxes) from both images
        const homeResult = await extractWithGoogleVision(homeImage, (progress) => {
          setProgress(prev => ({ ...prev, home: progress }));
        });

        const awayResult = await extractWithGoogleVision(awayImage, (progress) => {
          setProgress(prev => ({ ...prev, away: progress }));
        });



        // Parse using coordinate-based NBA 2K parser
        homeData = parseNBA2KBoxScoreWithCoordinates(homeResult.detections, players);
        
        awayData = parseNBA2KBoxScoreWithCoordinates(awayResult.detections, players);
        
      } else if (ocrMethod === 'claude') {
        // Use Claude Vision
        
        homeData = await extractWithClaudeVision(
          homeImage, 
          players,
          (progress) => setProgress(prev => ({ ...prev, home: progress })),
          homePlayers
        );
        
        awayData = await extractWithClaudeVision(
          awayImage,
          players,
          (progress) => setProgress(prev => ({ ...prev, away: progress })),
          awayPlayers
        );
      } else if (ocrMethod === 'gpt4') {
        // Use GPT-4 Vision
        
        homeData = await extractWithGPT4Vision(
          homeImage, 
          players,
          (progress) => setProgress(prev => ({ ...prev, home: progress }))
        );
        
        awayData = await extractWithGPT4Vision(
          awayImage,
          players,
          (progress) => setProgress(prev => ({ ...prev, away: progress }))
        );
      }

      // Validate stats
      const homeWarnings = homeData.players.map(p => ({
        player: p,
        warnings: validateStats(p)
      }));

      const awayWarnings = awayData.players.map(p => ({
        player: p,
        warnings: validateStats(p)
      }));

      // Return extracted data
      onExtractComplete({
        home: homeData,
        away: awayData,
        warnings: {
          home: homeWarnings.filter(w => w.warnings.length > 0),
          away: awayWarnings.filter(w => w.warnings.length > 0)
        },
        metadata: {
          method: ocrMethod,
          ...(homeData.metadata || {}),
          ...(awayData.metadata || {})
        }
      });

    } catch (err) {
      console.error('Screenshot processing error:', err);
      setError(err.message || 'Failed to process screenshots. Please try manual entry.');
    } finally {
      setProcessing(false);
      setProgress({ home: 0, away: 0 });
    }
  };

  const clearImage = (team) => {
    if (team === 'home') {
      setHomeImage(null);
      setHomePreview(null);
    } else {
      setAwayImage(null);
      setAwayPreview(null);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg dark:shadow-gray-900">
      <div className="mb-4">
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-2">
          📸 Upload NBA 2K Screenshots
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Upload box score screenshots from NBA 2K for automatic stat extraction. 
          Make sure player names and stats are clearly visible.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 dark:text-red-400 text-sm">⚠️ {error}</p>
        </div>
      )}

      {/* OCR Method Selection */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          📸 OCR Method
        </label>
        
        <div className="space-y-2">
          <label className={`flex items-center ${!googleAvailable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
            <input
              type="radio"
              name="ocrMethod"
              value="google"
              checked={ocrMethod === 'google'}
              onChange={(e) => setOcrMethod(e.target.value)}
              disabled={!googleAvailable}
              className="mr-3"
            />
            <div>
              <div className="font-medium">
                Google Cloud Vision 🌟 {!googleAvailable && '(Not Configured)'}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {googleAvailable 
                  ? 'Enterprise-grade OCR. Best for styled text. ~$0.001 per game.'
                  : 'Configure GOOGLE_APPLICATION_CREDENTIALS in .env to enable'
                }
              </div>
            </div>
          </label>
          
          <label className={`flex items-center ${!claudeAvailable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
            <input
              type="radio"
              name="ocrMethod"
              value="claude"
              checked={ocrMethod === 'claude'}
              onChange={(e) => setOcrMethod(e.target.value)}
              disabled={!claudeAvailable}
              className="mr-3"
            />
            <div>
              <div className="font-medium">
                Claude Vision ⭐ {!claudeAvailable && '(Not Configured)'}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {claudeAvailable 
                  ? 'Best for structured tables. ~$0.005 per game. Recommended.'
                  : 'Add ANTHROPIC_API_KEY in .env to enable'
                }
              </div>
            </div>
          </label>

          <label className={`flex items-center ${!gpt4Available ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
            <input
              type="radio"
              name="ocrMethod"
              value="gpt4"
              checked={ocrMethod === 'gpt4'}
              onChange={(e) => setOcrMethod(e.target.value)}
              disabled={!gpt4Available}
              className="mr-3"
            />
            <div>
              <div className="font-medium">
                GPT-4 Vision {!gpt4Available && '(Not Configured)'}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {gpt4Available 
                  ? 'AI-powered. ~$0.01 per game. May confuse columns.'
                  : 'Add OPENAI_API_KEY in .env to enable'
                }
              </div>
            </div>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Home Team Upload */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {homeTeam.name} (Home)
          </label>
          
          {!homePreview ? (
            <div
              onDrop={(e) => handleDrop(e, 'home')}
              onDragOver={handleDragOver}
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
            >
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileSelect(e.target.files[0], 'home')}
                className="hidden"
                id="home-upload"
              />
              <label htmlFor="home-upload" className="cursor-pointer">
                <div className="text-gray-400 mb-2">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Drop image here or click to upload
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  PNG, JPG up to 10MB
                </p>
              </label>
            </div>
          ) : (
            <div className="relative">
              <img
                src={homePreview}
                alt="Home team preview"
                className="w-full h-48 object-contain border border-gray-300 dark:border-gray-600 rounded-lg"
              />
              <button
                type="button"
                onClick={() => clearImage('home')}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              {processing && (
                <div className="absolute bottom-2 left-2 right-2 bg-black bg-opacity-75 rounded p-2">
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all"
                      style={{ width: `${progress.home * 100}%` }}
                    />
                  </div>
                  <p className="text-white text-xs mt-1 text-center">
                    Processing... {Math.round(progress.home * 100)}%
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Away Team Upload */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {awayTeam.name} (Away)
          </label>
          
          {!awayPreview ? (
            <div
              onDrop={(e) => handleDrop(e, 'away')}
              onDragOver={handleDragOver}
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
            >
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileSelect(e.target.files[0], 'away')}
                className="hidden"
                id="away-upload"
              />
              <label htmlFor="away-upload" className="cursor-pointer">
                <div className="text-gray-400 mb-2">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Drop image here or click to upload
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  PNG, JPG up to 10MB
                </p>
              </label>
            </div>
          ) : (
            <div className="relative">
              <img
                src={awayPreview}
                alt="Away team preview"
                className="w-full h-48 object-contain border border-gray-300 dark:border-gray-600 rounded-lg"
              />
              <button
                type="button"
                onClick={() => clearImage('away')}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              {processing && (
                <div className="absolute bottom-2 left-2 right-2 bg-black bg-opacity-75 rounded p-2">
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-blue-50 dark:bg-blue-900/200 h-2 rounded-full transition-all"
                      style={{ width: `${progress.away * 100}%` }}
                    />
                  </div>
                  <p className="text-white text-xs mt-1 text-center">
                    Processing... {Math.round(progress.away * 100)}%
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Process Button */}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => {
            clearImage('home');
            clearImage('away');
          }}
          disabled={processing || (!homeImage && !awayImage)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Clear All
        </button>
        <button
          type="button"
          onClick={processScreenshots}
          disabled={processing || !homeImage || !awayImage}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {processing ? (
            <>
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Extract Stats
            </>
          )}
        </button>
      </div>

      {/* Instructions */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
        <p className="text-sm text-blue-800 font-semibold mb-2">📋 Tips for best results:</p>
        <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1 list-disc list-inside">
          <li>Take clear, high-resolution screenshots</li>
          <li>Ensure player names and stats are fully visible</li>
          <li>Avoid glare or shadows on the screen</li>
          <li>Crop out unnecessary parts (focus on the box score table)</li>
          <li>You'll have a chance to review and correct the extracted data</li>
        </ul>
      </div>
    </div>
  );
};

export default ScreenshotUpload;
